import React from 'react';
import { motion } from 'framer-motion';
import cloudVariants from './TransientThoughtCloudVariants';

// Parse CSS linear-gradient to extract colors and stops for SVG
const parseGradient = (cssGradient) => {
  if (!cssGradient || typeof cssGradient !== 'string' || !cssGradient.startsWith('linear-gradient')) {
    return null;
  }

  // Extract angle (default 90deg if not specified)
  const angleMatch = cssGradient.match(/linear-gradient\((\d+)deg/);
  const angle = angleMatch ? parseInt(angleMatch[1], 10) : 90;

  // Convert angle to SVG coordinates (x1, y1, x2, y2)
  const angleRad = (angle - 90) * (Math.PI / 180);
  const x1 = Math.round(50 - Math.cos(angleRad) * 50);
  const y1 = Math.round(50 - Math.sin(angleRad) * 50);
  const x2 = Math.round(50 + Math.cos(angleRad) * 50);
  const y2 = Math.round(50 + Math.sin(angleRad) * 50);

  // Extract color stops - handle both rgba() and hex colors
  const colorStopRegex = /((?:rgba?\([^)]+\)|#[0-9a-fA-F]{3,8}))\s*(\d+)?%?/gi;
  const stops = [];
  let match;

  while ((match = colorStopRegex.exec(cssGradient)) !== null) {
    stops.push({
      color: match[1],
      offset: match[2] ? `${match[2]}%` : null
    });
  }

  // Auto-assign offsets if not specified
  if (stops.length > 0) {
    stops.forEach((stop, index) => {
      if (!stop.offset) {
        stop.offset = `${(index / (stops.length - 1)) * 100}%`;
      }
    });
  }

  return { x1, y1, x2, y2, stops };
};

// Get the first color from a gradient or return the color as-is
const getFirstColor = (color) => {
  if (!color || typeof color !== 'string') return color || '#FABBFF';
  if (!color.startsWith('linear-gradient')) return color;

  const parsed = parseGradient(color);
  if (parsed && parsed.stops.length > 0) {
    return parsed.stops[0].color;
  }
  return '#FABBFF';
};

const UserName = ({ name = '', fillColor, position, windowSize }) => {
  if (!name) return null;

  const fontSizeMap = {
    large: 'text-2xl',
    medium: 'text-xl',
    small: 'text-lg'
  };

  const positionMap = {
    br: 'absolute -bottom-4 -right-12',
    bl: 'absolute -bottom-4 -left-12',
    tr: 'absolute -top-4 -right-12',
    tl: 'absolute -top-4 -left-12'
  };

  const fontSize = fontSizeMap[windowSize];
  const positionClass = positionMap[position];

  // Handle gradient for avatar background
  const isGradient = fillColor && typeof fillColor === 'string' && fillColor.startsWith('linear-gradient');
  const avatarStyle = isGradient
    ? { background: fillColor }
    : { backgroundColor: fillColor };

  return (
    <div
      style={{
        padding: '4px 12px 4px 4px',
        width: 'fit-content',
        borderRadius: '4px',
        border: '1px solid #333333',
        background: 'rgba(34, 34, 34, 0.8)',
        height: 'fit-content'
      }}
      className={`flex items-center ${positionClass} mx-2`}>
      <div
        style={avatarStyle}
        className={`text-white px-2 rounded-sm mr-2 ${fontSize}`}>
        {name[0].toUpperCase()}
      </div>
      <p className={`text-white ${fontSize}`}>{name}</p>
    </div>
  );
};

const getCloudSvg = (config, fillColor, windowSize) => {
  if (!windowSize || !config.id) return null;

  const gradientData = parseGradient(fillColor);
  const gradientId = `ttGradient-${config.id}`;

  // Use gradient fill reference if gradient, otherwise use solid color
  const actualFill = gradientData ? `url(#${gradientId})` : fillColor;

  let baseSvg;
  if (config?.words?.length < 34) {
    baseSvg = cloudVariants[`small-${config.id}`](actualFill);
  } else if (config?.words?.length < 100) {
    baseSvg = cloudVariants[`medium-${config.id}`](actualFill);
  } else {
    // ≥100 chars (your "XL" tier) — always use the largest variant regardless
    // of window size so long sentences get the biggest bubble.
    baseSvg = cloudVariants[`large-${config.id}`](actualFill);
  }

  // If no gradient, return as-is
  if (!gradientData) return baseSvg;

  // Wrap SVG with gradient definition
  const { x1, y1, x2, y2, stops } = gradientData;

  // Parse viewBox to get dimensions for userSpaceOnUse gradient
  const viewBox = baseSvg.props.viewBox?.split(' ').map(Number) || [0, 0, 1096, 668];
  const [, , vbWidth, vbHeight] = viewBox;

  return (
    <svg
      width={baseSvg.props.width}
      height={baseSvg.props.height}
      viewBox={baseSvg.props.viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient
          id={gradientId}
          gradientUnits="userSpaceOnUse"
          x1={Math.round((x1 / 100) * vbWidth)}
          y1={Math.round((y1 / 100) * vbHeight)}
          x2={Math.round((x2 / 100) * vbWidth)}
          y2={Math.round((y2 / 100) * vbHeight)}
        >
          {stops.map((stop, index) => (
            <stop key={index} offset={stop.offset} stopColor={stop.color} />
          ))}
        </linearGradient>
      </defs>
      {baseSvg.props.children}
    </svg>
  );
};

const getTextPositionClasses = (limit, windowSize) => {
  const baseClasses = 'absolute w-[80%] left-[10%]';

  const verticalPositions = {
    'large-17': 'top-[15%] bottom-[15%]',
    'large-27': 'top-[15%] bottom-[15%]',
    'large-34': 'top-[16%] bottom-[14%]',
    'large-44': 'top-[18%] bottom-[12%]',

    'medium-17': 'top-[15%] bottom-[15%]',
    'medium-27': 'top-[15%] bottom-[15%]',
    'medium-34': 'top-[16%] bottom-[14%]',
    'medium-44': 'top-[18%] bottom-[12%]',

    'small-17': 'top-[15%] bottom-[15%]',
    'small-27': 'top-[15%] bottom-[15%]',
    'small-34': 'top-[16%] bottom-[14%]',
    'small-44': 'top-[18%] bottom-[12%]'
  };

  const key = `${windowSize}-${limit}`;
  return `${baseClasses} ${verticalPositions[key] || 'top-[15%] bottom-[15%]'}`;
};

const getLineClamp = (fontSize, windowSize) => {
  const size = parseFloat(fontSize.replace(/[^0-9.]/g, ''));

  const clampMap = {
    large: {
      4.5: 3,
      3.5: 4,
      2.5: 5,
      2: 6,
      default: 7
    },
    medium: {
      4.5: 2,
      3.5: 3,
      2.5: 4,
      2: 5,
      default: 6
    },
    small: {
      4.5: 1,
      3.5: 2,
      2.5: 3,
      2: 4,
      default: 5
    }
  };

  const sizeMap = clampMap[windowSize] || clampMap.small;

  if (size > 4.5) return sizeMap[4.5];
  if (size > 3.5) return sizeMap[3.5];
  if (size > 2.5) return sizeMap[2.5];
  if (size > 2) return sizeMap[2];
  return sizeMap.default;
};

const TransientThoughtCloud = ({
  config,
  fillColor,
  textColor,
  fontFamily,
  disableBubble,
  fontSize = '2.5rem',
  initiateAnimation,
  containerSize = 'large',
  transientThoughtShowUsername,
}) => {
  const handleAnimationComplete = (definition) => {
    initiateAnimation();
    disableBubble(definition);
  };

  // Use first color from gradient for drop shadow
  const shadowColor = getFirstColor(fillColor);

  return (
    <motion.div
      animate={config.animate}
      initial={{ opacity: 0, x: 0, y: 0 }}
      style={{
        filter: `drop-shadow(4px 6px 1px ${shadowColor})`
      }}
      transition={config.transition}
      onAnimationComplete={handleAnimationComplete}
      className={config.className}>

      {getCloudSvg(config, fillColor, containerSize)}

      {transientThoughtShowUsername && (
        <UserName
          position={config.id}
          fillColor={fillColor}
          name={config?.username}
          windowSize={containerSize}
        />
      )}

      <div
        lang="en"
        style={{
          fontSize,
          fontFamily,
          hyphens: 'auto',
          lineHeight: 1.2,
          color: textColor,
          overflow: 'hidden',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
        }}
        className={`flex justify-center items-center text-center ${getTextPositionClasses(config.limit, containerSize)}`}>

        <div style={{
          padding: '0 16px',
          overflow: 'hidden',
          display: '-webkit-box',
          textOverflow: 'ellipsis',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: getLineClamp(fontSize, containerSize),
        }}>
          {config.words}
        </div>
      </div>
    </motion.div>
  );
};

export default TransientThoughtCloud;
